'use client';

import React, { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import ClassInfo from './components/ClassInfo';
import VideoPlayer from './components/VideoPlayer';
import LiveClassroomInteractive from './components/LiveClassroomInteractive';
import { useAuth } from '@/contexts/AuthContext';
import { getBatchesForEnrolledCourses } from '@/services/batchService';

interface UpcomingClass {
  id: string;
  courseTitle: string;
  batchName?: string;
  instructor: string;
  date: string;
  time: string;
  duration: string;
  isLive: boolean;
  zoomLink?: string;
}

const LiveClassroomPage = () => {
  const { user } = useAuth();
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadUpcomingClasses() {
      if (!user) {
        console.log('⚠️ [LiveClassroom] No user logged in');
        setLoading(false);
        return;
      }

      try {
        console.log('🚀 [LiveClassroom] Loading upcoming classes for user:', user.id);
        setLoading(true);

        // Fetch ALL batches for enrolled courses
        const { data: allBatchesForCourses, error: batchFetchError } = await getBatchesForEnrolledCourses(user.id);

        if (batchFetchError) {
          console.error('❌ [LiveClassroom] Batch fetch error:', batchFetchError);
          setUpcomingClasses([]);
          return;
        }

        console.log('📦 [LiveClassroom] Fetched batches:', {
          count: allBatchesForCourses?.length || 0,
          batches: allBatchesForCourses?.map(b => ({
            id: b.id,
            name: b.batch_name,
            course: b.course_title,
            startDate: b.start_date,
            endDate: b.end_date,
            schedule: b.schedule_info
          }))
        });

        // Filter for currently active classes (batch start <= now <= batch end)
        const now = new Date();

        const activeClasses = (allBatchesForCourses || [])
          .filter((batch: any) => {
            const batchStart = new Date(batch.start_date);
            const batchEnd = new Date(batch.end_date);
            
            // Show only if batch is currently active (started but not ended)
            return batchStart <= now && batchEnd >= now && batch.is_active;
          })
          .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

        console.log('✨ [LiveClassroom] Active classes after filtering:', activeClasses.length);

        const classes: UpcomingClass[] = activeClasses.map((batch: any) => {
          const startDate = new Date(batch.start_date);
          const endDate = new Date(batch.end_date);
          
          const classTime = batch.schedule_info?.time || 'Time not set';
          const classDays = batch.schedule_info?.days?.join(', ') || 'Days not set';
          
          // Check if class is currently live (scheduled today)
          const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
          const isScheduledToday = batch.schedule_info?.days?.includes(currentDay) || false;
          const isLive = isScheduledToday;

          const zoomLink = batch.schedule_info?.zoom_url || '';

          return {
            id: batch.id,
            courseTitle: batch.course_title || 'Course',
            batchName: batch.batch_name,
            instructor: batch.instructor_name || 'Instructor',
            date: startDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
            time: `${classDays} at ${classTime}`,
            duration: `${Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7))} weeks`,
            isLive: isLive,
            zoomLink: zoomLink
          };
        });

        console.log('🎓 [LiveClassroom] Final active classes:', classes.length);
        setUpcomingClasses(classes);

      } catch (err: any) {
        console.error('❌ [LiveClassroom] Unexpected error:', err);
        setUpcomingClasses([]);
      } finally {
        setLoading(false);
      }
    }

    loadUpcomingClasses();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 pb-16 px-4 lg:px-8 max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-24 pb-16 px-4 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-headline font-bold text-foreground mb-2">
            Live Classes
          </h1>
          <p className="text-muted-foreground">
            Join your currently active live classes
          </p>
        </div>

        {upcomingClasses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-muted-foreground text-lg">
              You don't have any active classes at the moment.
            </p>
            <p className="text-muted-foreground mt-2">
              Enroll in a course to start attending live classes.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <VideoPlayer />
              <ClassInfo
                title={upcomingClasses[0].courseTitle}
                instructor={upcomingClasses[0].instructor}
                topic="Live Session"
                description="Join your instructor and classmates for an interactive learning session."
                duration={upcomingClasses[0].duration}
              />
            </div>
            
            <div className="lg:col-span-1">
              <LiveClassroomInteractive upcomingClasses={upcomingClasses} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LiveClassroomPage;